import {Formik, Form} from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formikControl/FormikControl'
import toast from 'react-hot-toast'
import {createTrip, updateTrip} from './api'
import {fetchDriverList} from '../driver/api'
import {fetchShiftList} from '../shift/api'
import {fetchRootList} from '../roots/api'
import {useQueries} from 'react-query'
import {RunnerBall} from '../../../components/FormLoader'
import {fetchBusList} from '../bus/api'
import {addMinutes, format, isAfter, isValid, parse} from 'date-fns'
import {handleApiError} from '../../../helpers/errorHandler'
import {Col, Row} from 'react-bootstrap'
import {fetchTripNumberList} from '../tripNumber/api'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'
import {fetchConductorList} from '../conductor/api'

function removeSecondsValueFromTime(timeStr) {
  if (!timeStr) return ''

  let parsed

  // Try 'HH:mm:ss' first
  parsed = parse(timeStr, 'HH:mm:ss', new Date())
  if (!isValid(parsed)) {
    // Fallback to 'HH:mm'
    parsed = parse(timeStr, 'HH:mm', new Date())
  }

  return isValid(parsed) ? format(parsed, 'HH:mm') : timeStr
}

function TripForm({initialValues, onAfterSubmit}) {
  const formType = initialValues ? 'update' : 'add'

  const calls = [
    {queryKey: 'shift', queryFn: fetchShiftList, refetchOnWindowFocus: false},
    {queryKey: 'driver', queryFn: fetchDriverList, refetchOnWindowFocus: false},
    {queryKey: 'bus', queryFn: fetchBusList, refetchOnWindowFocus: false},
    {queryKey: 'routes', queryFn: fetchRootList, refetchOnWindowFocus: false},
    {queryKey: 'tripNumberList', queryFn: fetchTripNumberList, refetchOnWindowFocus: false},
    {queryKey: 'conductors', queryFn: fetchConductorList, refetchOnWindowFocus: false},
  ]
  const results = useQueries(calls)

  if (
    results[0].isFetching ||
    results[1].isFetching ||
    results[2].isFetching ||
    results[3].isFetching ||
    results[4].isFetching ||
    results[5].isFetching
  ) {
    return <RunnerBall />
  }
  const shiftList = results[0]?.data?.data ?? []
  const driverList = results[1]?.data?.data ?? []
  const busList = results[2]?.data?.data ?? []
  const routeList = results[3]?.data?.data ?? []
  const tripNumbers = results[4]?.data?.data ?? []
  const conductors = results[5]?.data?.data ?? []
  const tripTypes = [{tripType: 'pickup'}, {tripType: 'drop'}]

  const submitHandler = (values, {setSubmitting: setLoading}) => {
    setLoading(true)
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createTrip : updateTrip
    values.trip_name = (values?.trip_number || '') + '-' + (values?.route_name || '')
    action(values)
      .then(() => {
        toast.success('Success', {id: toastId})
        onAfterSubmit()
      })
      .catch((err) => {
        handleApiError(err, toastId)
      })
      .then((res) => setLoading(false))
  }

  const formValues = initialValues
    ? {
        ...initialValues,
        start_time: removeSecondsValueFromTime(initialValues.start_time),
        end_time: removeSecondsValueFromTime(initialValues.end_time),
      }
    : {
        shift_id: '',
        driver_id: '',
        conductor_id: '',
        bus_id: '',
        route_id: '',
        start_time: '',
        end_time: '',
        trip_number: '',
        trip_name: '',
        trip_status: '',
        trip_type: '',
      }

  return (
    <div className="px-5 py-3">
      <Formik
        initialValues={formValues}
        validationSchema={Yup.object({
          trip_name: Yup.string()
            // .required('Trip Name is required')
            .max(100, 'The Shift name cannot exceed 100 characters'),
          trip_number: Yup.string().required('Trip Number is required'),
          route_id: Yup.string().required('Route is required'),
          conductor_id: Yup.string().required('Bus Attendee is required'),
          driver_id: Yup.string().required('Driver is required'),
          route_name: Yup.string(),
          trip_status: Yup.string().required('Trip Status is required'),
          trip_type: Yup.string().required('Trip Type is required'),
          start_time: Yup.string().required('Start Time is required'),
          end_time: Yup.string()
            .test(
              'is-at-least-30-minutes-after',
              'End Time must be at least 30 minutes after Start Time',
              function (value) {
                const {start_time} = this.parent

                // If start_time or end_time is not provided, skip validation
                if (!start_time || !value) return true

                // Parse start_time and end_time into Date objects
                const parsedStartTime = parse(start_time, 'HH:mm', new Date())
                const parsedEndTime = parse(value, 'HH:mm', new Date())

                // Add 30 minutes to the start time
                const startTimePlusMins = addMinutes(parsedStartTime, 29)

                // Check if end_time is at least 30 minutes after start_time
                return isAfter(parsedEndTime, startTimePlusMins)
              }
            )
            .required('End Time is required'),
        })}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, setFieldValue, values, dirty, isValid}) => (
          <Form>
            <Row>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={tripNumbers}
                  placeholder="Trip Number"
                  label="Trip Number"
                  name="trip_number"
                  value={tripNumbers.find((trip) => trip.trip_number === values.trip_number) || ''}
                  optionLabel={'trip_number'}
                  optionValue={'trip_number'}
                  disabled={formType === 'update'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={routeList}
                  placeholder="Select Route"
                  label="Route"
                  name="route_id"
                  value={routeList.find((route) => route.route_id === values.route_id) || ''}
                  optionLabel={'route_name'}
                  optionValue={'route_id'}
                  required
                  disabled={formType === 'update'}
                  onChange={(selected) => {
                    setFieldValue('route_id', selected?.route_id || '')
                    setFieldValue('route_name', selected?.route_name || '')
                  }}
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="input"
                  placeholder="Trip Name"
                  label="Trip Name"
                  name="trip_name"
                  disabled
                  value={(values?.trip_number || '') + ' - ' + (values?.route_name || '')}
                />
              </Col>

              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={tripTypes}
                  placeholder="Select Trip Type"
                  name="trip_type"
                  label="Trip Type"
                  value={tripTypes.find((trip) => trip.tripType === values.trip_type) || ''}
                  optionLabel={'tripType'}
                  getOptionLabel={(option) => {
                    return option.tripType.charAt(0).toUpperCase() + option.tripType.slice(1)
                  }}
                  optionValue={'tripType'}
                  disabled={formType === 'update'}
                  required
                />
              </Col>
              {console.log(tripTypes)}
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={busList}
                  placeholder="Select Bus"
                  label="Bus"
                  name="bus_id"
                  value={busList.find((bus) => bus.bus_id === values.bus_id) || ''}
                  optionLabel={'bus_name'}
                  getOptionLabel={(option) => `${option.bus_name} - ${option.bus_number}`}
                  optionValue={'bus_id'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={shiftList}
                  placeholder="Select Shift"
                  label="Shift"
                  name="shift_id"
                  value={shiftList.find((shift) => shift.shift_id === values.shift_id) || ''}
                  optionLabel={'shift_name'}
                  optionValue={'shift_id'}
                  disabled={formType === 'update'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="input"
                  required
                  type="time"
                  placeholder="Start Time"
                  label="Start Time"
                  name="start_time"
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="input"
                  required
                  type="time"
                  placeholder="End Time"
                  label="End Time"
                  name="end_time"
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={driverList}
                  placeholder="Select Driver"
                  label="Driver"
                  name="driver_id"
                  value={driverList.find((driver) => driver.driver_id === values.driver_id) || ''}
                  optionLabel={'driver_name'}
                  getOptionLabel={(option) => option.driver_name + ' - ' + option.driver_phone}
                  optionValue={'driver_id'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={conductors}
                  placeholder="Select Bus Attendee"
                  label="Bus Attendee"
                  name="conductor_id"
                  value={
                    conductors.find(
                      (conductor) => conductor.conductor_id === values.conductor_id
                    ) || ''
                  }
                  optionLabel={'conductor_name'}
                  optionValue={'conductor_id'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    {key: 'Active', value: '1'},
                    {key: 'Inactive', value: '0'},
                  ]}
                  required
                  placeholder="Active Status"
                  label="Active Status"
                  name="trip_status"
                />
              </Col>
            </Row>
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default TripForm
